<template>
  <SettingsCard
    :title="$t('settings.email.salutation-settings.title')"
    :subtitle="$t('settings.email.salutation-settings.subtitle')"
    icon="mdi-chat"
    :model-value="isOpen"
    @update:modelValue="setOpen"
  >
    <v-row class="ma-2">
      <v-col
        cols="12"
        md="6"
      >
        <div class="font-weight-bold">
          {{ $t('settings.email.salutation-settings.body-texts.0') }}
        </div>
        <div
          class="text-grey"
          v-html="sanitizeHtml($t('settings.email.salutation-settings.body-texts.1'))"
        />
      </v-col>
      <v-col
        cols="12"
        md="6"
      >
        <TextForm
          :fields="salutationFields"
          :disabled="showUpselling"
          @update="updateCustomWording"
        />
        <div class="d-flex justify-end">
          <v-btn
            variant="outlined"
            :disabled="!hasCustomWording() || showUpselling"
            @click="updateCustomWording({ salutation: '', farewell: '' })"
          >
            {{ $t('labels.reset') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>
    <BlurredBackdropUpselling v-if="showUpselling" />
  </SettingsCard>
</template>

<script>
import DOMPurify from 'dompurify'

import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import SettingsCard from '@/components/SettingsCard.vue'
import TextForm from '@/components/TextForm.vue'
import { useQueryOpen } from '@/composables/useQueryOpen'
import eventBus, { eventNames } from '@/lib/eventBus'
import featureMixin from '@/mixins/feature'

import UPDATE_COMPANY_CUSTOM_WORDING from './queries/UpdateCompanyCustomWording.gql'

export default {
  emits: ['refetchCompany'],
  setup() {
    const { isOpen, setOpen } = useQueryOpen({ settingName: 'farewell' })
    return {
      isOpen,
      setOpen,
    }
  },
  components: { SettingsCard, TextForm, BlurredBackdropUpselling },
  mixins: [featureMixin],
  props: {
    company: {
      type: Object,
      default: () => ({}),
    },
    showUpselling: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      customWording: {
        salutation: this.company?.customWording?.salutation || '',
        farewell: this.company?.customWording?.farewell || '',
      },
    }
  },
  computed: {
    salutationFields() {
      return [
        {
          label: this.$t('settings.email.salutation-settings.labels.salutation'),
          name: 'salutation',
          placeholder: this.$t('settings.email.salutation-settings.labels.salutation-placeholder'),
          required: false,
          value: this.customWording.salutation,
        },
        {
          label: this.$t('settings.email.salutation-settings.labels.farewell'),
          name: 'farewell',
          placeholder: this.$t('settings.email.salutation-settings.labels.farewell-placeholder'),
          required: false,
          value: this.customWording.farewell,
        },
      ]
    },
  },
  watch: {
    company: {
      deep: true,
      handler() {
        this.customWording.farewell = this.company.customWording?.farewell || ''
        this.customWording.salutation = this.company.customWording?.salutation || ''
      },
    },
  },
  methods: {
    sanitizeHtml(html) {
      return DOMPurify.sanitize(html)
    },
    hasCustomWording() {
      return Boolean(this.customWording.salutation || this.customWording.farewell)
    },
    async updateCustomWording(value) {
      this.isLoading = true
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_COMPANY_CUSTOM_WORDING,
          variables: {
            input: {
              companyId: this.$auth.user.companyId,
              salutation: this.customWording.salutation,
              farewell: this.customWording.farewell,
              ...value,
            },
          },
        })
        this.$tracking.event('Settings', 'Clicked', 'Update Company Custom Wording')
        eventBus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'success',
          text: this.$t('alerts.settings.email.salutation-settings.success'),
        })
        this.$emit('refetchCompany')
      } catch (err) {
        eventBus.$emit(eventNames.SHOW_SNACKBAR, {
          color: 'error',
          text: this.$t('alerts.settings.email.salutation-settings.error'),
        })
      } finally {
        this.isLoading = false
      }
    },
  },
}
</script>
